import Link from 'next/link'
import { Briefcase, Clock, MapPin } from 'lucide-react'
import Script from 'next/script'
import { useId } from 'react'
import MetaTags from '@/components/MetaTags'
import { organization } from '@/constants/organization'
import { jobs } from '@/data/jobs'

export async function getStaticPaths() {
  const paths = jobs.map((job) => ({
    params: { slug: job.slug },
  }))
  return { paths, fallback: false }
}

export async function getStaticProps({ params }) {
  // Ensure params and params.slug are defined
  if (!params || !params.slug) {
    return { notFound: true }
  }
  const job = jobs.find((job) => job.slug === params.slug)
  if (!job) {
    return { notFound: true }
  }
  // Return only serializable metadata (no JSX)
  return { props: { jobMeta: { title: job.title, slug: job.slug } } }
}

const JobPage = ({ jobMeta }) => {
  const jobJsonLdId = useId()

  // Guard against missing jobMeta
  if (!jobMeta) {
    return <div>Error: Missing job data</div>
  }
  // Retrieve the full job object (including JSX) at runtime
  const fullJob = jobs.find((job) => job.slug === jobMeta.slug)
  if (!fullJob) {
    return <div>Error: Job not found</div>
  }

  // Create JSON-LD using fullJob data with defaults.
  const jsonLd = JSON.stringify({
    '@context': 'https://schema.org/',
    '@type': 'JobPosting',
    title: fullJob.title,
    description: fullJob.description,
    identifier: {
      '@type': 'PropertyValue',
      name: 'Ventura Web Design & marketing',
      value: fullJob.slug,
    },
    datePosted: fullJob.datePosted || '2024-04-10',
    jobLocationType: 'TELECOMMUTE',
    employmentType: 'FULL_TIME',
    hiringOrganization: {
      '@type': 'Organization',
      '@id': organization.id,
      name: organization.name,
      sameAs: organization.url,
      logo: organization.logo,
    },
    jobLocation: fullJob.location || {
      '@type': 'Place',
      address: {
        '@type': 'PostalAddress',
        ...organization.address,
      },
    },
  })

  return (
    <>
      <MetaTags
        pageTitle={fullJob.title}
        pageDescription={`Job details for ${fullJob.title}`}
        path={`/careers/${fullJob.slug}`}
        isHome={false}
      />
      <Script
        id={jobJsonLdId}
        type="application/ld+json"
        // biome-ignore lint/security/noDangerouslySetInnerHtml: Required for JSON-LD structured data
        dangerouslySetInnerHTML={{ __html: jsonLd }}
      />
      <div className="border-b py-10">
        <main className="mx-auto max-w-screen-xl px-5 md:px-10">
          <div className="mb-5 flex flex-wrap gap-4">
            <div className="flex items-center gap-2 text-sm opacity-50">
              <Briefcase className="text-xs size-4" /> Job Position
            </div>
            <div className="flex items-center gap-2 text-sm opacity-50">
              <MapPin className="text-xs size-4" /> Remote
            </div>
            <div className="flex items-center gap-2 text-sm opacity-50">
              <Clock className="text-xs size-4" /> Full-Time
            </div>
          </div>
          <h1 className="mb-4 text-3xl font-bold">{fullJob.title}</h1>
          <div className="mb-10">{fullJob.description}</div>
          <div className="mb-3 flex w-full flex-wrap gap-2">
            <Link
              href="mailto:careers@venturawebdesign.com"
              className="group flex w-full items-center justify-center gap-2 rounded-2xl bg-vblue2 px-5 py-4 text-white transition hover:bg-vblue1 lg:w-auto"
            >
              <h2 className="font-bold leading-none">Email Us Your Resume</h2>
            </Link>
            <Link
              href="/careers"
              className="group flex w-full items-center justify-center gap-2 rounded-2xl border bg-white px-5 py-4 lg:w-auto"
            >
              <h2 className="font-bold leading-none">See all jobs</h2>
            </Link>
          </div>
          <p className="text-sm">
            To apply, send your resume to{' '}
            <Link href="mailto:careers@venturawebdesign.com">
              careers@venturawebdesign.com
            </Link>
            .
          </p>
        </main>
      </div>
    </>
  )
}

export default JobPage
